import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { alpha, useTheme } from '@mui/material/styles';
import { forwardRef, useId, useRef } from 'react';
import { useDisclosure, useMergeRefs } from '../../hooks';
import { IconPickerDropdown, type IconPickerDropdownProps } from './dropdown';
import { IconPickerIcon } from './icon';

export interface IconPickerProps
  extends Omit<IconPickerDropdownProps, 'onClose'> {
  id?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const IconPicker = forwardRef<HTMLButtonElement, IconPickerProps>(
  ({ id, label, error, helperText, ...props }, ref) => {
    const { palette } = useTheme();
    const internalRef = useRef<HTMLButtonElement>(null);
    const mergedRef = useMergeRefs(ref, internalRef);
    const autoId = useId();
    const pickerId = id ?? autoId;
    const { isOpen, onOpen, onClose } = useDisclosure();

    const borderColor = isOpen
      ? palette.slate.main
      : alpha(palette.slate[400], 0.32);

    return (
      <FormControl error={error}>
        {label && (
          <FormLabel id={pickerId} sx={{ mb: 0.5 }}>
            {label}
          </FormLabel>
        )}
        <IconButton
          ref={mergedRef}
          onClick={onOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 56,
            height: 56,
            border: '1px solid',
            borderColor: error ? 'error.main' : borderColor,
            borderRadius: 1,
            '&:hover': {
              borderColor: error ? 'error.main' : palette.slate.main,
            },
          }}
        >
          <IconPickerIcon icon={props.value} />
        </IconButton>
        <Popover
          id={id}
          open={isOpen}
          anchorEl={internalRef.current}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <IconPickerDropdown {...props} onClose={onClose} />
        </Popover>
        {helperText && (
          <FormHelperText id={`${pickerId}-helper`}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);

if (process.env.NODE_ENV !== 'production') {
  IconPicker.displayName = 'IconPicker';
}
